import {
  convert, Instant, LocalDate,
  LocalDateTime,
  ZonedDateTime,
  ZoneId,
  ZoneOffset
} from '@js-joda/core';
import '@js-joda/timezone';
import { i18n } from '@lingui/core';
import dayjs, { Dayjs } from 'dayjs';
import { isNil, path } from 'ramda';

export { InstantUtils, makeInstant } from './instant';
export { makeDate } from './js-date';
export { makeLocalDate, makeLocalDateUtils } from './local-date';
export { makeLocalDateTime, makeLocalDateTimeUtils } from './local-date-time';
export { makeZonedDateTime, makeZonedDateTimeUtils } from './zoned-date-time';

export const DEFAULT_TIMEZONE = ZoneId.of('Europe/London');

// ZoneId.of('UTC') rather than ZoneOffset.UTC: Intl rejects the offset id "Z".
const UTC_ZONE = ZoneId.of('UTC');

const tryZone = (id?: string | null): ZoneId | undefined => {
  if (!id) return undefined;
  try {
    return ZoneId.of(id);
  } catch {
    return undefined;
  }
};

/** First candidate that is a known zone id, else UTC. */
export const licenceZone = (...zoneIds: (string | null | undefined)[]): ZoneId => zoneIds
  .map(tryZone)
  .find((zone): zone is ZoneId => !!zone) ?? UTC_ZONE;

export interface LicencedProject {
  timeZone?: string | null;
  licence?: { expiryDate?: Instant | null; acceptedTimeZone?: string | null } | null;
}

/**
 * A project licence expiry as a calendar day. The licence form stores the chosen day as midnight in the project's
 * time zone (Mine Location > Time Zone) and the server records that zone on the licence, so rendering the instant in
 * the recorded zone shows exactly the day the admin picked, for every user everywhere. Licences saved before the
 * project zone was honoured carry "Europe/London" with a midnight-UTC instant, which resolves to the same day.
 */
export const licenceExpiryZoned = (project: LicencedProject): ZonedDateTime | undefined => {
  const expiryDate = project.licence?.expiryDate;
  if (!expiryDate) return undefined;
  return expiryDate.atZone(licenceZone(project.licence?.acceptedTimeZone, project.timeZone));
};

export const isTemporal = (testTemporal: any) => testTemporal instanceof ZonedDateTime
  || testTemporal instanceof LocalDateTime
  || testTemporal instanceof LocalDate
  || testTemporal instanceof Date;

export const DEFAULT_SHORT_DATE_CONFIG: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/** Short date without a timezone suffix, for dates that must read the same for every user (e.g. licence expiry). */
export const DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG: Intl.DateTimeFormatOptions = {
  ...DEFAULT_SHORT_DATE_CONFIG,
  timeZoneName: undefined,
};

export const DEFAULT_LONG_DATE_TIME_CONFIG: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

export const DEFAULT_SHORT_DATE_TIME_CONFIG: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};
export const DEFAULT_TIMEZONE_CONFIG: Intl.DateTimeFormatOptions = {
  timeZone: 'short',
};

// The app uses the non-standard "gr" code for Greek; Intl needs the ISO "el".
const INTL_LOCALE_ALIASES: Record<string, string> = { gr: 'el' };

/** The active UI language as a BCP 47 tag for Intl formatting (falls back to the browser locale). */
export const getIntlLocale = (): string | undefined => {
  const { locale } = i18n;

  if (!locale) return undefined;

  return INTL_LOCALE_ALIASES[locale] ?? locale;
};

export const displayTemporal = (temporal: ZonedDateTime | LocalDate | LocalDateTime | Date, options?: Intl.DateTimeFormatOptions) => {
  const jsDate = temporal instanceof Date ? temporal : convert(temporal, temporal instanceof ZonedDateTime ? temporal.zone() : undefined).toDate();

  return jsDate.toLocaleDateString(getIntlLocale(), {
    timeZoneName: 'short',
    ...options,
    timeZone: temporal instanceof ZonedDateTime ? temporal.zone().id() : undefined,
  });
};

interface DateSortOptions {
  dateProp?: string | string[];
  asc: boolean;
}

export const dateSort = ({ dateProp, asc = true }: DateSortOptions) => {
  let propPath: string[] | undefined;

  if (dateProp) {
    propPath = Array.isArray(dateProp) ? dateProp : [dateProp];
  }

  return (a: any, b: any) => {
    const aDate = propPath ? path(propPath, a) : a as ZonedDateTime | LocalDate | LocalDateTime | Date;
    const bDate = propPath ? path(propPath, b) : b as ZonedDateTime | LocalDate | LocalDateTime | Date;

    if (typeof aDate !== typeof bDate) {
      throw new Error(i18n._('You cannot sort difference date types'));
    }

    if (isNil(aDate) || isNil(bDate)) {
      return 0;
    }

    const left = asc ? aDate : bDate;
    const right = asc ? bDate : aDate;

    if (left instanceof ZonedDateTime && right instanceof ZonedDateTime) {
      return left.compareTo(right);
    }

    if (left instanceof LocalDate && right instanceof LocalDate) {
      return left.compareTo(right);
    }

    if (left instanceof LocalDateTime && right instanceof LocalDateTime) {
      return left.compareTo(right);
    }

    if (left instanceof Instant && right instanceof Instant) {
      return left.compareTo(right);
    }

    if (left instanceof Date && right instanceof Date) {
      return left.getTime() - right.getTime();
    }

    throw new Error(i18n._('Unsupported date type'));
  };
};

const SECONDS_IN_A_DAY = 86400;
export const dayJsToLocalDate = (date: Dayjs) => LocalDate.of(date.year(), date.month() + 1, date.date());
export const LocalDateToDayJs = (date: LocalDate) => dayjs.unix(date.toEpochDay() * SECONDS_IN_A_DAY);

export const dayJsToLocalDateTime = (date: Dayjs) => LocalDateTime.of(date.year(), date.month() + 1, date.date());
export const LocalDateTimeToDayJs = (date: LocalDateTime) => dayjs.unix(date.toEpochSecond(ZoneOffset.UTC));
